import React, { useRef, useState } from 'react';
import { usePickBrands, BrandPick } from './TagBrandBits';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { X, Loader2, Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { tagThemeClass } from './tagTheme';

const BTN = 'inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors duration-150 disabled:opacity-60';

function ImportResult({ result }) {
  if (!result) return null;
  return (
    <div className="mt-4 space-y-2" data-testid="tagws-import-result">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm text-foreground" data-testid="tagws-import-created-count">
        <CheckCircle2 size={15} className="text-emerald-600" /> {result.created_count} tag(s) created
      </div>
      {result.skipped?.length > 0 && (
        <div className="max-h-48 overflow-y-auto rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2" data-testid="tagws-import-skipped-list">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-destructive"><AlertTriangle size={13} /> {result.skipped.length} skipped</div>
          {result.skipped.map((s, i) => (
            <div key={i} className="text-xs text-foreground"><span className="font-mono">{s.jewellery_no || `Row ${s.row}`}</span> — {s.reason}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// Download the Excel template, fill it, upload it back → manual tag cards.
export function TagImportDialog({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await notesApi.get('/notes/tag-workspace/import/template', { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = 'jewellery-tags-template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(extractError(e, 'Could not download the template.'));
    } finally {
      setDownloading(false);
    }
  };

  const pickBrands = usePickBrands();
  const [brandId, setBrandId] = useState('');
  const upload = async (file) => {
    if (pickBrands.length && !brandId) { toast.error('Choose the brand for these tags first.'); return; }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('brand_id', brandId);
      const { data } = await notesApi.post('/notes/tag-workspace/import', fd);
      setResult(data);
      if (data.created_count) { toast.success(`${data.created_count} tag(s) imported.`); onImported?.(); }
      else toast.error('No tags were imported.');
    } catch (e) {
      toast.error(extractError(e, 'Import failed.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const close = () => { setResult(null); onClose(); };
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-import-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={close} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-border bg-card p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground"><FileSpreadsheet size={18} className="text-[hsl(var(--ring))]" /> Import tags from Excel</h3>
          <button onClick={close} aria-label="Close" data-testid="tagws-import-close-button" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <ol className="mb-4 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>Download the template and fill one row per stone line (repeat the Jewellery No for more stones).</li>
          <li>Net weight is calculated automatically (Gross − total carats ÷ 5).</li>
          <li>Upload the filled file — each Jewellery No becomes a card.</li>
        </ol>
        <div className="mb-3"><BrandPick brands={pickBrands} value={brandId} onChange={setBrandId} testid="tagws-import-brand-select" label="Brand of the imported tags" /></div>
        <div className="flex flex-wrap gap-2">
          <button onClick={download} disabled={downloading} className={`${BTN} border border-border bg-background text-foreground hover:bg-secondary`} data-testid="tagws-import-download-template-button">
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download template
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={busy} className={`${BTN} bg-primary text-primary-foreground hover:opacity-90`} data-testid="tagws-import-upload-button">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload filled file
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="tagws-import-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }} />
        </div>
        <ImportResult result={result} />
      </div>
    </div>
  );
}
